import { Button, Divider, Drawer, List, Paper, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import { GeoJsonGeometryType, PointRoadFeature, RoutePartOfRoadFeatures, RoutePartOfRoadFeaturesWithBruksklasse } from "../../services/RuteplanAPI";
import { RoadFeatureResponse, useRouteDrawerOpen, useRouteResponse, useRouteStoreActions, useSelectedRouteIndex } from "../../stores/RouteStore";
import Bomstasjon from "./Bomstasjoner";
import Rasteplasser from "./Rasteplasser";
import Dognhvileplasser from "./Dognhvileplasser";
import Vegsperringer from "./Vegsperringer";
import VarutsatteVeger from "./VarutsatteVeger";
import Ferries from "./Ferries";
import Turistveger from "./Turistveger";
import Vegkameraer from "./Vegkameraer";
import LengthRestrictions from "./LengthRestrictions";
import HeightRestrictions from "./HeightRestrictions";
import WeightRestrictions from "./WeightRestrictions";
import { useRouteHighlightActions } from "../../stores/RouteHighlightStore";
import { useMap } from "react-leaflet";
import { RouteSelector } from "./RouteSelector";
import { MissingBruksklasseItem } from "./MissingBruksklasse";
import { ManeuverType } from "./ManeuverType";
import RouteSummary from "./RouteSummary";
import ManeuverLabel from "./ManeuverLabel";
import { useRouteFeatureDialogStoreActions } from "../../stores/RouteFeatureDialogStore";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { RouteDownload } from "./RouteDownload/RouteDownload";
import { GetRouteName } from "../../services/RouteNameService";
import { SpesialTransportVeggruppeItem } from "./SpesialTransportVeggruppe";
import { Trafikkmeldinger } from "./Trafikkmeldinger";
import Ladestasjoner from "./Ladestasjoner";
import { RoadFeaturesAPIView } from "./RoadFeaturesAPIView/RoadFeaturesAPIView";
import MeasurementSites from "./MeasurementSites";
import JernbanekryssningMedFare from "./JernbanekryssningMedFare";
import Tunneler from "./Tunneler";
import { useCallback } from "react";
//TODO: enable when NVDB is ready: import Bruer from "./Bruer";
import Bruer from "./Bruer";
import Trafikkreguleringer from "./Trafikkreguleringer";
import GjennomkjoringForbudter from "./GjennomkjoringForbudter";

export type RoutePart = RoutePartOfRoadFeatures | RoutePartOfRoadFeaturesWithBruksklasse

export interface IRoutePartProps {
    roadFeatures: RoadFeatureResponse | undefined;
    onClick: (feature: any, event: React.MouseEvent) => void;
    onMouseEnterItem: (feature: PointRoadFeature) => void;
    onMouseLeaveItem: () => void; 
}

export default function RouteDescription() {
    const { setFeature, setClickedFeature } = useRouteHighlightActions();
    const { setClickedRoadFeature, setRoadFeatureDialogIsOpen} = useRouteFeatureDialogStoreActions();
    const routeDrawerOpen = useRouteDrawerOpen();
    const routeStoreActions = useRouteStoreActions();
    const routeResponse = useRouteResponse();
    const selectedRouteIndex = useSelectedRouteIndex();
    const map = useMap();
    const highlighter = useRouteHighlightActions();

    const onRoadFeatureClick = (feature: any, event: React.MouseEvent) => { 
        event.stopPropagation(); 
        setClickedRoadFeature(feature);
        setRoadFeatureDialogIsOpen(true);        
    }

    const onMouseEnterItem = useCallback((item: PointRoadFeature) => {
        if(item.location == null) {
            return;
        }

        if(item.location.geometry != null && 
            item.location.geometry.type !== undefined && 
            item.location.geometry.type === GeoJsonGeometryType.LINE_STRING) {

            highlighter.setRoadFeatureHighlightLine(item.location.geometry);

        } else {
            highlighter.setRouteDescriptionHighlightFeature(item);
        }
    }, [highlighter]);

    const onMouseLeaveItem = useCallback(() => {
        highlighter.setRouteDescriptionHighlightFeature(null);
        highlighter.setRoadFeatureHighlightLine(null);
    }, [highlighter]);
        
    const mouseOver = () => {
        map.scrollWheelZoom.disable();
    };

    const mouseExit = ()  => {
        map.scrollWheelZoom.enable();
    }

    const getDrawerButtonStyle = () => {
        if (routeDrawerOpen) {
            return("drawerButton drawerOpenCloseButton drawerButton--right")
        }
        return("drawerButton drawerOpenCloseButton");
    }
   
    return (
        <>     
            <Button className={getDrawerButtonStyle()} onClick={() => routeStoreActions.setRouteDrawerOpen(!routeDrawerOpen)}>
                {routeDrawerOpen ? 
                <ArrowBackIosNewIcon />
                : 
                <ArrowForwardIosIcon />
                }
            </Button>
            <Drawer variant="persistent" anchor="left" open={routeDrawerOpen} >
                <Paper elevation={0} style={{zIndex: "9999", overflowY:"auto", width:"300px", overflowX:"hidden", marginLeft: "4px"}} onMouseOver={mouseOver} onMouseLeave={mouseExit}>
                    <Typography align="center" variant="h6" marginTop="8px">Beregnet rute</Typography>
                    <RouteSelector/>
                    {routeResponse && routeResponse.routes.length > 0 && (
                        <RouteDownload route={routeResponse.routes[selectedRouteIndex]} fileName={GetRouteName(routeResponse, selectedRouteIndex)}/>
                    )}         
                    <RoadFeaturesAPIView/>
                    
                    {routeResponse && routeResponse.routes.length > 0 && (
                    <RouteSummary route={routeResponse.routes[selectedRouteIndex]} />                                
                    )}

                    <Divider />

                    <Stepper activeStep={-1} orientation="vertical" style={{paddingLeft: "10px"}}>
                        {routeResponse && routeResponse.routes[selectedRouteIndex].features.map( (feature, index) => {
                            return (
                                <Step 
                                    expanded={true} 
                                    key={"roadFeaturesListItemContainer" + index} 
                                    onMouseEnter={() => {setFeature(feature)}} 
                                    onMouseLeave={() => {setFeature(null)}} 
                                    onClick={() => {setClickedFeature(feature)}}
                                    style={{display: "flex", flexDirection: "column", alignItems:"start", cursor: "pointer"}}>

                                    <StepLabel style={{cursor:"pointer"}} icon={<ManeuverType maneuverType={feature.properties.maneuverType} />}>
                                        <ManeuverLabel maneuverText={feature.properties.maneuverText} />
                                    </StepLabel>
                                    
                                    <StepContent>
                                        <List key={"roadFeaturesListItemList" + index} style={{paddingBottom: "0px", paddingTop: "0px"}}>
                                            <Bomstasjon roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Dognhvileplasser roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Ladestasjoner roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Vegsperringer roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <VarutsatteVeger roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Ferries roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Rasteplasser roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Trafikkmeldinger roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Turistveger roadFeatures={feature.properties.roadFeatures}onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Vegkameraer roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <MeasurementSites roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <Tunneler roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <HeightRestrictions roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <LengthRestrictions roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <WeightRestrictions roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <MissingBruksklasseItem roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <SpesialTransportVeggruppeItem roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <JernbanekryssningMedFare roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem}/>
                                            <GjennomkjoringForbudter roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem} />
                                            <Trafikkreguleringer roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem} />
                                            {/*
                                            //TODO: enable when NVDB is ready
                                            <Bruer roadFeatures={feature.properties.roadFeatures} onClick={onRoadFeatureClick}/>
                                            */}
                                        </List>
                                    </StepContent>
                                </Step>                        
                            )
                        })}
                    </Stepper>
                </Paper>
            </Drawer>
        </>       
    );
}
